import _ from "lodash";
import React, { PureComponent } from "react";
import { ArrowIcon } from "SvgComponents";
import I18nHOC from "UIComponents/I18nHOC";
import Loading from "UIComponents/Loading";

import NoDataComponent from "UIComponents/NoDataComponent";
import {
  isExportTypePreferredAsFile,
  getExportLinks,
  getDriveShareTemplateOptions,
  getFileType,
} from "Utils";
import update from "immutability-helper";
import classes from "./DriveShareModal.scss";
import PropTypes from "prop-types";
import { getExportTypeIndex } from "./DriveShareModalUtils";
import {
  MediaCard,
  Dropdown,
  DropdownMenu,
  FilterLabelButton,
  IconButton,
  Button,
  Modal,
} from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import { getSettingValue } from "modules/PermissionModule";
import { getUserEntityType, getUserId } from "modules/CommonSelectors";
import { updateSettings } from "modules/ConfigurationThunks";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import ACLStore from "lib/aclStore";

const DRIVE_SHARE_USER_PREFERENCE_CONFIG = {
  fileExportableSetting: "DriveShareSettingForFileExportables",
  fileNonExportableSetting: "DriveShareSettingForNonFileExportables",
  featureFlag: "FeatureFlag:DriveShareFileTypeSetting",
};

const getShareModalTypes = ({ serviceType }) => {
  if (_.isEqual(serviceType, "MICROSOFT")) {
    return {
      RESOURCE: {
        label: "common:sharing_with_onedrive",
        subText: "common:sharing_with_drive_subtext_msg",
      },
      STUDENT_TEMPLATE: {
        label: "common:student_template_onedrive_share_label",
      },
      STUDENT_DRIVE: {
        label: "common:add_from_onedrive",
        subText: "common:adding_from_onedrive_msg",
      },
      PROJECT_GROUP_REPORT_TEMPLATE: {
        label: "common:add_from_onedrive",
      },
    };
  }
  return {
    RESOURCE: {
      label: "common:sharing_with_google_drive",
      subText: "common:sharing_with_drive_subtext_msg",
    },
    STUDENT_TEMPLATE: {
      label: "common:student_template_drive_share_label",
    },
    STUDENT_DRIVE: {
      label: "common:add_from_google_drive",
      subText: "common:drive_share_modal_subtext",
    },
    PROJECT_GROUP_REPORT_TEMPLATE: {
      label: "common:sharing_with_google_drive",
    },
  };
};

const styles = {
  itemTitleContainerStyle: { maxWidth: "235px" },
  modalBodyContainerStyle: { padding: "0px 24px" },
};
const modalWidth = "604px";
const modalHeight = "586px";

class DriveShareModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      files: [],
      isLoading: false,
    };
  }

  componentDidMount = async () => {
    const {
      files,
      accessToken,
      sourceType,
      onClickClose,
      t,
      defaultExportType,
      attachmentExportOptions,
      serviceType,
      userType,
      userId,
      userEntityType,
    } = this.props;

    this.setState({ isLoading: true });

    const requests = _.map(files, async file => {
      let exportOptions = [];
      let exportAs = "";
      let permission = "";
      let exportTypeIndex = -1;
      switch (sourceType) {
        case "STUDENT_TEMPLATE":
          {
            exportOptions = getDriveShareTemplateOptions({ file, serviceType });

            exportTypeIndex = getExportTypeIndex({
              exportOptions,
              exportType: defaultExportType,
              userType,
            });

            exportAs = _.get(exportOptions, `[${exportTypeIndex}]exportAs`, "");
            permission = _.get(exportOptions, `[${exportTypeIndex}]value`, "");
          }
          break;
        default: {
          const userPreferenceSetting = ACLStore.can(
            DRIVE_SHARE_USER_PREFERENCE_CONFIG.featureFlag
          )
            ? {
                fileExportable: getSettingValue({
                  name:
                    DRIVE_SHARE_USER_PREFERENCE_CONFIG.fileExportableSetting,
                  userId,
                  userEntityType,
                  readFromUserAndOrgLevel: true,
                }),
                fileNonExportable: getSettingValue({
                  name:
                    DRIVE_SHARE_USER_PREFERENCE_CONFIG.fileNonExportableSetting,
                  userId,
                  userEntityType,
                  readFromUserAndOrgLevel: true,
                }),
              }
            : {};

          exportOptions = await getExportLinks({
            file,
            accessToken,
            serviceType,
            userType,
            userPreferenceSetting,
          });

          if (!_.isEmpty(attachmentExportOptions)) {
            exportOptions = _.filter(exportOptions, option =>
              _.includes(attachmentExportOptions, option.value)
            );
          }

          exportTypeIndex = getExportTypeIndex({
            exportOptions,
            exportType: defaultExportType,
          });

          exportAs = _.get(exportOptions, `[${exportTypeIndex}]value`, "");
        }
      }
      return {
        ...file,
        exportOptions: _.map(exportOptions, option => {
          return {
            ...(option || {}),
            label: t(option.label),
            subText: option?.subText ? t(option.subText) : undefined,
          };
        }),
        url: _.get(exportOptions, `[${exportTypeIndex}]url`, ""),
        mimeType: _.get(exportOptions, `[${exportTypeIndex}]mimeType`, ""),
        exportAs,
        value: _.get(exportOptions, `[${exportTypeIndex}]value`, ""),
        permission,
      };
    });

    Promise.all(requests)
      .then(files => this.setState({ files, isLoading: false }))
      .catch(err => {
        // console.log("err", err);
        onClickClose();
      });
  };

  onDropdownItemClick = ({ exportFormat, id }) => {
    const { sourceType } = this.props;
    const { files } = this.state;
    const fileIndex = _.findIndex(files, { id });

    const selectedDropDownObj = _.find(files[fileIndex].exportOptions, {
      value: exportFormat,
    });
    const { value, url, mimeType } = selectedDropDownObj;
    let { exportAs } = selectedDropDownObj;

    //value as export template in source type RESOURCE
    if (sourceType != "STUDENT_TEMPLATE") {
      exportAs = value;
    }

    let permission = undefined;
    //check permission from dropdown
    if (sourceType == "STUDENT_TEMPLATE" && exportAs != "FILE") {
      permission = value;
    }

    this.setState({
      files: update(files, {
        [fileIndex]: {
          value: { $set: value },
          exportAs: { $set: exportAs },
          url: { $set: url },
          mimeType: { $set: mimeType },
          permission: { $set: permission },
        },
      }),
    });
  };

  removeSelectedDocument = ({ id }) => {
    const { files } = this.state;
    const fileIndex = _.findIndex(files, file => file.id == id);

    this.setState({ files: update(files, { $splice: [[fileIndex, 1]] }) });
  };

  setExportTypeUserPreference = ({ files, isFileType }) => {
    const { defaultExportType, updateSettings, sourceType } = this.props;

    const fileExportTypes = _.map(files, file => {
      const exportOptions = _.get(file, "exportOptions", []);
      if (_.size(exportOptions) > 0) {
        return _.get(file, "exportAs", "");
      }
    });

    const uniqExportTypes = _.uniq(_.compact(fileExportTypes));

    if (
      _.isEmpty(defaultExportType) &&
      sourceType != "STUDENT_TEMPLATE" &&
      _.size(uniqExportTypes) === 1 &&
      !_.isEmpty(uniqExportTypes[0])
    ) {
      updateSettings({
        settings: [
          {
            name: isFileType
              ? DRIVE_SHARE_USER_PREFERENCE_CONFIG.fileExportableSetting
              : DRIVE_SHARE_USER_PREFERENCE_CONFIG.fileNonExportableSetting,
            value: uniqExportTypes[0],
          },
        ],
        showToastMsg: false,
      });
    }
  };

  setFileExportSelectionPreference = ({ files }) => {
    const [
      exportTypePreferredAsFileArray = [],
      exportTypePreferredAsLinkArray = [],
    ] = _.partition(files, file =>
      isExportTypePreferredAsFile({
        type:
          _.get(getFileType(_.get(file, "mimeType")) || {}, "text") ||
          _.get(file, "type"),
        mimeType: _.get(file, "mimeType"),
      })
    );

    this.setExportTypeUserPreference({
      files: exportTypePreferredAsFileArray,
      isFileType: true,
    });
    this.setExportTypeUserPreference({
      files: exportTypePreferredAsLinkArray,
      isFileType: false,
    });
  };

  onClickDone = e => {
    e.stopPropagation();

    const { processChosenFiles, onClickClose } = this.props;
    const { files } = this.state;
    if (ACLStore.can(DRIVE_SHARE_USER_PREFERENCE_CONFIG.featureFlag)) {
      this.setFileExportSelectionPreference({ files });
    }

    setTimeout(() => {
      processChosenFiles({ files });
    }, 100);

    onClickClose();
  };

  onModalClick = e => {
    e.stopPropagation();
  };

  onModalKeyDown = event => {
    if (event.code === "Enter") {
      event.stopPropagation();
    }
  };

  getButtonComponnent = option => {
    return (
      <div className={classes.filterBox}>
        <div className={classes.filterText}>{`${option?.label}`}</div>
        <div className={classes.bottomArrow}>
          <ArrowIcon width={10} height={5} />
        </div>
      </div>
    );
  };

  getMediaCardOptions = ({
    exportOptions,
    exportAs,
    fileId,
    isDropdownDisabled = false,
    value,
  }) => {
    const mappedExportOptions = _.map(exportOptions, option => ({
      key: _.get(option, "value"),
      label: _.get(option, "label"),
      subText: _.get(option, "subText", undefined),
    }));

    const onMenuItemClick = selectedItem => {
      this.onDropdownItemClick({
        exportFormat: selectedItem?.key,
        id: fileId,
      });
    };

    // this will wrap text to next line if it is longer
    const hasMultilineOptions = _.some(exportOptions, option => {
      return !_.isEmpty(option?.subText);
    });

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <Dropdown
          dsVersion={"2.0"}
          overlay={
            <DropdownMenu
              dsVersion={"2.0"}
              onClick={onMenuItemClick}
              options={mappedExportOptions}
              hasMultilineOptions={hasMultilineOptions}
              itemTitleContainerStyle={
                hasMultilineOptions ? styles.itemTitleContainerStyle : {}
              }
            />
          }
        >
          <FilterLabelButton
            dsVersion={"2.0"}
            type={"outlined"}
            size={"medium"}
            disabled={isDropdownDisabled}
            labelValue={_.get(
              _.find(exportOptions, option => _.get(option, "value") == value),
              "label",
              "No options available"
            )}
          />
        </Dropdown>
        <IconButton
          dsVersion={"2.0"}
          icon={<DeleteOutlined />}
          variant={"neutral"}
          type={"plain"}
          size={"small"}
          style={{ marginLeft: "12px" }}
          onClick={() => this.removeSelectedDocument({ id: fileId })}
        />
      </div>
    );
  };

  render() {
    const {
      t,
      onClickClose,
      sourceType,
      serviceType,
      customStyle,
      testId,
    } = this.props;
    const { files, isLoading } = this.state;
    const shareModalTypes = getShareModalTypes({
      serviceType,
    });
    const { label, subText } = _.get(shareModalTypes, sourceType, {});
    const getIsSingleImportOptionForAllFiles = _.memoize(
      () => !_.size(_.filter(files, file => _.size(file?.exportOptions) > 1))
    );
    return (
      <Modal
        onClose={onClickClose}
        width={modalWidth}
        height={modalHeight}
        shouldCloseOnEsc={true}
        dsVersion="2.0"
        zIndex={customStyle.zIndex}
      >
        <Modal.Header
          isClosable
          title={t(label)}
          subtitle={!getIsSingleImportOptionForAllFiles() && t(subText)}
        />
        <Modal.Body isScrollable={true} style={styles.modalBodyContainerStyle}>
          <div
            className={classes.bodyContainer}
            onKeyDown={this.onModalKeyDown}
            onClick={this.onModalClick}
          >
            {_.isEmpty(files) && !isLoading && (
              <NoDataComponent
                emptyText=""
                isSearch={false}
                emptyHeaderText={t("classRoom:no_templates_selected")}
              />
            )}

            {!_.isEmpty(files) && (
              <div className={classes.scrollContainer}>
                {_.map(files, file => {
                  const mimeType = _.get(file, "mimeType");
                  const fileType =
                    _.get(file, "type") == "photo"
                      ? "IMAGE"
                      : getFileType(mimeType).text;

                  // Disable if export option is limited to one
                  const isDropdownDisabled =
                    _.size(_.get(file, "exportOptions")) <= 1;

                  return (
                    <div className={classes.fileContainer} key={file.id}>
                      <MediaCard
                        cardType="medium"
                        dsVersion={"2.0"}
                        name={_.get(file, "name", "file")}
                        type={fileType}
                        size={
                          _.get(file, "sizeBytes")
                            ? _.get(file, "sizeBytes")
                            : _.get(file, "size")
                        }
                        mimeType={mimeType}
                        url={_.get(file, "url")}
                        options={this.getMediaCardOptions({
                          exportAs: _.get(file, "exportAs"),
                          exportOptions: _.get(file, "exportOptions"),
                          fileId: _.get(file, "id"),
                          isDropdownDisabled,
                          value: _.get(file, "value"),
                        })}
                        testId={testId}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {isLoading && <Loading />}
          </div>
        </Modal.Body>
        <Modal.Footer hasSeparator>
          <div
            onKeyDown={this.onModalKeyDown}
            className={classes.rightButtonContainer}
          >
            <Button
              variant={"neutral"}
              type={"outlined"}
              size={"medium"}
              dsVersion={"2.0"}
              onClick={onClickClose}
              testId={`${testId}:drive_share:cancel`}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant={"progressive"}
              size={"medium"}
              dsVersion={"2.0"}
              onClick={this.onClickDone}
              testId={`${testId}:drive_share:done`}
            >
              {t("common:done")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

DriveShareModal.propTypes = {
  files: PropTypes.array,
  onClickClose: PropTypes.func,
  processChosenFiles: PropTypes.func,
  serviceType: PropTypes.string,
  userType: PropTypes.string,
  customStyle: PropTypes.object,
};

DriveShareModal.defaultProps = {
  files: [],
  sourceType: "RESOURCE",
  serviceType: "GOOGLE",
  customStyle: { zIndex: 1151 },
};

const mapStateToProps = (state, ownProps) => {
  const userId = getUserId();
  const userEntityType = getUserEntityType();
  return {
    userId,
    userEntityType,
  };
};

const mapActionCreators = {
  updateSettings,
};

export default compose(
  I18nHOC({ resource: ["common", "educatorCenter", "classRoom"] }),
  connect(mapStateToProps, mapActionCreators)
)(DriveShareModal);
